import React, { useState, memo, useEffect, useCallback } from 'react';
import styled from 'styled-components/macro';
import CircularProgress from '@mui/material/CircularProgress';
import { isNil, sortBy, isEmpty, chunk } from 'lodash';

import { grey50 } from 'variables/colors';
import { border } from 'variables/general';
import { useSnackBar } from 'context/SnackBarContext';
import Button from 'components/ui/Button';
import { FlexCenter, FlexCenterVertical } from 'styles/flexbox';
import { getStaffAvailability } from 'api/staff';
import StaffCard from '../StaffCard';
import PaginationNav from './PaginationNav';

const Section = styled.section`
  &:not(:last-of-type) {
    margin-bottom: 3em;
    padding-bottom: 3em;
    border-bottom: ${border};
  }
`;

const SectionHeader = styled(FlexCenterVertical)``;

const SectionTitle = styled.h6`
  margin: 3em 0;
  color: ${grey50};
`;

const EmptyHeading = styled.h5`
  margin: 3em 0;
  text-align: center;
`;

const LoadingContainer = styled(FlexCenter)`
  width: 100%;
  padding: 2em 0;
`;

const StaffButton = styled(Button)`
  height: 2.5em;
  margin-left: 1em;
  padding: 1em 0.5em;
  font-size: 0.75em;
`;

const SectionWrapper = ({ title, isStaffList, fetchStaffList, children }) => (
  <Section>
    <SectionHeader>
      <SectionTitle>{title}</SectionTitle>

      {isStaffList && (
        <StaffButton
          color="primary"
          size="small"
          variant="outlined"
          type="button"
          onClick={fetchStaffList}
        >
          Get Staff
        </StaffButton>
      )}
    </SectionHeader>

    {children}
  </Section>
);

const StaffListSection = ({
  title,
  staffList,
  fetchStaffList,
  clinicStaff,
  sublocation,
  isStaffList,
  setStaffList,
  setSelectedStaff,
  startDate,
  endDate,
  startTime,
  endTime,
  setChanges,
}) => {
  if (isEmpty(staffList)) {
    return (
      <SectionWrapper
        title={title}
        isStaffList={isStaffList}
        staffList={staffList}
        fetchStaffList={fetchStaffList}
      >
        <EmptyHeading>No staff to show</EmptyHeading>
      </SectionWrapper>
    );
  }

  const clinicStaffIds = clinicStaff.map(({ id }) => id);
  const [staffWithAvailability, setStaffWithAvailability] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedStaffList = chunk(staffList, 10);
  const currentPageStaffList = paginatedStaffList[page];
  const currentPageStaffIds = currentPageStaffList.map(({ id }) => id);

  const fetchStaffAvailability = useCallback(
    async (staffIds) => {
      const { data } = await getStaffAvailability(
        staffIds,
        startDate,
        endDate,
        startTime,
        endTime,
      );

      return data;
    },
    [currentPageStaffIds],
  );

  useEffect(async () => {
    if (staffList && sublocation) {
      const staffData = await fetchStaffAvailability(currentPageStaffIds);

      const {
        address: subLocationAddress,
        city: subLocationCity,
        state: subLocationState,
        country: subLocationCountry,
        postal_code: subLocationPostalCode,
      } = sublocation || {};

      const staffWithAvail = currentPageStaffList.map((obj, index) => {
        const { available } = staffData[index];
        const { id } = obj;

        return {
          ...obj,
          available: clinicStaffIds.includes(id) ? 'true' : available,
        };
      });

      const formattedStaffList = await Promise.all(
        staffWithAvail.map(async (staff) => {
          const { address, city, state, postalCode, country } = staff;
          const postalCodeLabel = postalCode ? `, ${postalCode}` : '';

          // initialize services
          const service = new window.google.maps.DistanceMatrixService();

          // build request
          const origin = `${address}, ${city}, ${state}${postalCodeLabel}, ${country}`;
          const destination = `${subLocationAddress}, ${subLocationCity}, ${subLocationState}, ${subLocationPostalCode}, ${subLocationCountry}`;

          const request = {
            origins: [origin],
            destinations: [destination],
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.METRIC,
            avoidHighways: false,
            avoidTolls: false,
          };

          let returnValue = staff;
          await service.getDistanceMatrix(request).then(({ rows }) => {
            const { distance, duration } = rows[0].elements[0];
            if (distance || duration) {
              returnValue = {
                ...staff,
                distance: distance.text,
                travelDuration: duration.text,
              };
            }
          });

          return returnValue;
        }),
      );

      setStaffWithAvailability(formattedStaffList);
    }
  }, [staffList, sublocation, page]);

  const sortedStaff = sortBy(staffWithAvailability, ({ distance }) => {
    if (distance) {
      return Number(
        parseFloat(distance.replace(',', '').replace(/,/g, '.')).toFixed(1),
      );
    }
  });

  return (
    <SectionWrapper
      title={title}
      isStaffList={isStaffList}
      staffList={staffList}
      page={page}
      setPage={setPage}
      rowsPerPage={rowsPerPage}
      setRowsPerPage={setRowsPerPage}
      fetchStaffList={fetchStaffList}
    >
      {sortedStaff.map((staff) => (
        <StaffCard
          staff={staff}
          isStaffList={isStaffList}
          setStaffList={setStaffList}
          setSelectedStaff={setSelectedStaff}
          setChanges={setChanges}
        />
      ))}

      <PaginationNav
        count={staffList.length}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
      />
    </SectionWrapper>
  );
};

export default memo(StaffListSection);
